// /terms, /privacy, /acceptable-use: the three legal documents, in plain English. Public pages
// in the Hero 2 look (hx): a serif title, an "In short" summary, then numbered sections with
// anchors (#billing, #rights, …) in one readable column. Each links to the other two.

import { useEffect, type ReactNode } from 'react';
import { Link } from '../router';
import { HxPage } from './hx';
import './legal.css';

export type LegalDoc = 'terms' | 'privacy' | 'acceptable-use';

const SUPPORT_EMAIL = 'hello@cmx.sh';
const COMPANY = 'AFG Ventura Teknologi Pte. Ltd.';
const UPDATED = '12 October 2026';

/** The support address, as a mailto link. */
const Email = () => <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>;

const L = ({ to, children }: { to: string; children: ReactNode }) => <Link to={to}>{children}</Link>;

type Section = { id: string; title: string; body: ReactNode };
type Doc = { path: string; nav: string; name: string; title: ReactNode; intro: ReactNode; summary: ReactNode[]; sections: Section[] };

function Table({ head, rows, className = '' }: { head: string[]; rows: ReactNode[][]; className?: string }) {
  return (
    <div className="lg-table-wrap">
      <table className={`lg-table ${className}`}>
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// name, vCPU, memory GB, disk GB, USD per month (a year is 10 months).
const PLANS: [string, number, number, number, number][] = [
  ['Standard S', 2, 8, 100, 12],
  ['Standard M', 4, 16, 200, 25],
  ['Standard L', 8, 32, 400, 50],
];

// ---------------------------------------------------------------------------------------------
// Terms of Service
// ---------------------------------------------------------------------------------------------

const TERMS: Doc = {
  path: '/terms',
  nav: 'Terms',
  name: 'Terms of Service',
  title: (
    <>
      Terms of <em>Service</em>
    </>
  ),
  intro: (
    <>
      These terms are the agreement between you and {COMPANY} (“cmx”, “we”, “us”), a company registered in Singapore. They
      cover cmx at cmx.sh: the web app, the Mac app, the iPhone app and the <code>cmx</code> command line tool. By creating
      an account you agree to them.
    </>
  ),
  summary: [
    <>cmx rents you an always-on cloud Linux machine. What you put on it is yours.</>,
    <>You pay in advance, monthly or yearly. It renews until you cancel, and you can cancel any time.</>,
    <>If you cancel or stop paying, your cloud machine stops, and later its disk is deleted. Keep your own backups.</>,
    <>You're root on your cloud machine, so you're responsible for what runs on it, including your AI agents.</>,
    <>
      Don't use it to harm anyone. The <L to="/acceptable-use">Acceptable Use Policy</L> lists what's off limits.
    </>,
  ],
  sections: [
    {
      id: 'service',
      title: 'The service',
      body: (
        <>
          <p>
            cmx gives you your own Linux virtual machine in Amazon Web Services, in Singapore (ap-southeast-1). It stays on when
            your own computer is closed, so AI coding agents can keep working. You reach it from the web app, the Mac app,
            the iPhone app and the CLI.
          </p>
          <p>
            We run cmx on a best-effort basis. There is no service level agreement (SLA) and no uptime guarantee. We work
            hard to keep it running and to fix problems fast, but outages can happen.
          </p>
          <p>We improve cmx all the time, so features may change, be added or be removed.</p>
        </>
      ),
    },
    {
      id: 'accounts',
      title: 'Your account',
      body: (
        <ul>
          <li>
            Sign up with an email and password (we confirm it with a code we email you), or with Google or GitHub.
          </li>
          <li>You must be 18 or older, or the age of majority where you live if that's higher.</li>
          <li>One account per person.</li>
          <li>Use an email address you read. It's how we reach you about billing and your cloud machine.</li>
          <li>Keep your sign-in details safe. You're responsible for what happens under your account.</li>
        </ul>
      ),
    },
    {
      id: 'billing',
      title: 'Plans and payment',
      body: (
        <>
          <Table
            className="lg-plans"
            head={['Plan', 'Machine', 'Month', 'Year']}
            rows={PLANS.map(([name, cpu, mem, disk, month]) => [
              name,
              <span className="lg-spec">
                <span>{cpu} vCPU</span>
                <span>{mem} GB memory</span>
                <span>{disk} GB disk</span>
              </span>,
              `$${month}`,
              `$${month * 10}`,
            ])}
          />
          <ul>
            <li>Prices are in US dollars. A yearly plan costs 10 times the monthly price, so two months are free.</li>
            <li>
              You pay in advance, through Stripe. Your subscription renews automatically at the end of each period until
              you cancel.
            </li>
            <li>There is no free trial. A promotion code may discount a plan or make it free.</li>
            <li>
              Changing size is prorated. Moving to a bigger machine charges the difference straight away. Moving to a
              smaller one gives you a credit on your next invoice.
            </li>
            <li>Taxes may be added where the law requires them.</li>
          </ul>
        </>
      ),
    },
    {
      id: 'cancelling',
      title: 'Cancelling and refunds',
      body: (
        <ul>
          <li>Cancel any time from the billing page.</li>
          <li>Your plan keeps running until the end of the period you've paid for. Then your cloud machine stops.</li>
          <li>We keep its disk for 14 days, then delete it. Deleted data can't be recovered.</li>
          <li>We don't refund partial periods, except where the law requires it.</li>
        </ul>
      ),
    },
    {
      id: 'non-payment',
      title: 'If a payment fails',
      body: (
        <>
          <ol className="lg-timeline">
            <li>
              <b>Payment fails.</b> We retry it and email you.
            </li>
            <li>
              <b>7 days unpaid.</b> We stop your cloud machine. Its disk is kept.
            </li>
            <li>
              <b>14 more days stopped.</b> We delete the cloud machine and its disk, and your plan ends.
            </li>
          </ol>
          <p>We email you at every step. If you get one of these emails, check your payment details on the billing page.</p>
        </>
      ),
    },
    {
      id: 'content',
      title: 'Your content',
      body: (
        <>
          <p>
            Everything on your cloud machine is yours: your code, files, data and whatever your agents make. We don't claim any
            ownership of it. You let us store and process it only as needed to run cmx for you.
          </p>
          <p>
            We don't look at it, except to keep the service running, to investigate abuse or a security problem, or when
            the law requires. Machines are isolated from each other.
          </p>
          <p>
            <b>Back up what matters.</b> The disks of the hosts our machines run on have daily snapshots, kept for 7 days.
            They are for our own disaster recovery, not a backup service for you. You're responsible for your own backups,
            for example by pushing your code to a git remote.
          </p>
        </>
      ),
    },
    {
      id: 'responsibilities',
      title: 'Your responsibilities',
      body: (
        <ul>
          <li>You're root on your cloud machine. You decide what runs on it, and you're responsible for it.</li>
          <li>
            That includes AI agents acting on your behalf. What an agent does on your cloud machine counts as what you do.
          </li>
          <li>
            Keep the credentials you put on your cloud machine safe: API keys, tokens, SSH keys and passwords.
          </li>
          <li>
            Follow the <L to="/acceptable-use">Acceptable Use Policy</L> and the law.
          </li>
        </ul>
      ),
    },
    {
      id: 'suspension',
      title: 'Suspension and ending',
      body: (
        <>
          <p>
            We may suspend your cloud machine or close your account for abuse (see the{' '}
            <L to="/acceptable-use#enforcement">Acceptable Use Policy</L>) or for non-payment (see{' '}
            <a href="#non-payment">If a payment fails</a>).
          </p>
          <p>
            You can leave any time: cancel your plan, then delete your account in the app under Account → Delete account.
          </p>
        </>
      ),
    },
    {
      id: 'liability',
      title: 'Warranties and liability',
      body: (
        <>
          <p>
            cmx is provided “as is” and “as available”. To the extent the law allows, we make no promises beyond those in
            these terms.
          </p>
          <p>
            To the extent the law allows, we're not liable for indirect losses such as lost data, profits or business.
            Nothing here limits liability that the law doesn't allow us to limit.
          </p>
        </>
      ),
    },
    {
      id: 'changes',
      title: 'Changes to these terms',
      body: (
        <p>
          We may update these terms. We'll post the new version here and change the date at the top. If a change matters to
          you, we'll email you before it takes effect.
        </p>
      ),
    },
    {
      id: 'law',
      title: 'Governing law',
      body: (
        <p>
          These terms are governed by the laws of Singapore. If something goes wrong, please write to us first. Most things
          can be sorted out that way.
        </p>
      ),
    },
    {
      id: 'contact',
      title: 'Contact',
      body: (
        <p>
          {COMPANY}, Singapore. Email <Email />.
        </p>
      ),
    },
  ],
};

// ---------------------------------------------------------------------------------------------
// Privacy Policy
// ---------------------------------------------------------------------------------------------

const PRIVACY: Doc = {
  path: '/privacy',
  nav: 'Privacy',
  name: 'Privacy Policy',
  title: (
    <>
      Privacy <em>Policy</em>
    </>
  ),
  intro: (
    <>
      This policy explains what personal data cmx collects, why, who helps us handle it, and what you can do about it.{' '}
      {COMPANY} in Singapore is responsible for your data. It applies to the web app, the Mac app, the iPhone app and the
      CLI.
    </>
  ),
  summary: [
    <>We collect what we need to run your account, your cloud machine and your billing.</>,
    <>We don't sell your data, and we don't use ad trackers.</>,
    <>What's on your cloud machine is yours. We don't look at it except to keep it running, to deal with abuse, or by law.</>,
    <>A few providers help us run cmx. Each is listed below, with what it does.</>,
    <>Delete your account any time and your data goes with it, except billing records the law makes us keep.</>,
  ],
  sections: [
    {
      id: 'who',
      title: 'Who we are',
      body: (
        <p>
          cmx is run by {COMPANY}, a company registered in Singapore (“we”, “us”). We decide how your personal data is used,
          and we're the ones to contact about it: <Email />.
        </p>
      ),
    },
    {
      id: 'collect',
      title: 'What we collect, and why',
      body: (
        <Table
          head={['What', 'Why']}
          rows={[
            [
              'Email address, and a hash of your password (never the password itself)',
              'To create your account and sign you in.',
            ],
            [
              'Your Google or GitHub account, if you sign in with one',
              'To sign you in. We only use them if you choose to.',
            ],
            ['Sign-in records and IP addresses', 'To keep your account secure and to rate-limit abuse.'],
            [
              'Your plan, subscription status and invoices',
              <>To bill you. Stripe handles your card; we don't store card numbers.</>,
            ],
            [
              'Machine usage: CPU, memory, disk and network, plus latency measurements',
              'To run your cloud machine, spot problems and keep cmx fast.',
            ],
            ['Push-notification device tokens (iPhone app)', 'To send you notifications.'],
            [
              'Voice input and session names, only when you use these features',
              'Sent through OpenRouter to an AI model, to transcribe your speech and to name your sessions.',
            ],
          ]}
        />
      ),
    },
    {
      id: 'machine',
      title: 'What’s on your cloud machine',
      body: (
        <>
          <p>
            Your files, code and the work of your agents belong to you. We don't look at them, except to keep the service
            running, to investigate abuse or a security problem, or when the law requires. Machines are isolated from each
            other.
          </p>
          <p>
            The disks of the hosts our machines run on have daily snapshots, kept for 7 days, for disaster recovery. See{' '}
            <L to="/terms#content">Terms: Your content</L>.
          </p>
        </>
      ),
    },
    {
      id: 'dont',
      title: 'What we don’t do',
      body: (
        <ul>
          <li>We don't sell your data, to anyone.</li>
          <li>We don't use advertising trackers.</li>
          <li>We don't store your card number.</li>
          <li>We don't share your data except with the providers below, or when the law requires.</li>
        </ul>
      ),
    },
    {
      id: 'providers',
      title: 'Who helps us',
      body: (
        <>
          <p>These providers process data for us, only for the purpose listed:</p>
          <Table
            head={['Provider', 'What for']}
            rows={[
              ['Amazon Web Services', 'Hosting: your cloud machine, in Singapore.'],
              ['Google Cloud', 'Hosting: our servers, in Jakarta, Indonesia.'],
              ['Stripe', 'Payments.'],
              ['Resend', 'Sending email, such as sign-in codes and billing notices.'],
              ['OpenRouter', 'Routing to AI models for voice transcription and session names.'],
              ['Apple', 'Push notifications to the iPhone app.'],
              ['Google, GitHub', 'Signing in, only if you choose to sign in with them.'],
            ]}
          />
          <p>Some of these providers process data outside Indonesia and Singapore.</p>
        </>
      ),
    },
    {
      id: 'retention',
      title: 'How long we keep it',
      body: (
        <ul>
          <li>We keep your account data for as long as your account exists.</li>
          <li>
            When you delete your account (in the app: Account → Delete account), we delete your data. Billing records are
            the exception: we keep them as long as the law requires.
          </li>
          <li>
            When a machine is deleted, its disk is gone. Host snapshots that held it expire within 7 days.
          </li>
        </ul>
      ),
    },
    {
      id: 'rights',
      title: 'Your rights',
      body: (
        <>
          <p>
            You can ask to see the personal data we hold about you, correct it, delete it, or get a copy to take elsewhere.
            Email <Email /> and we'll help.
          </p>
          <p>
            We follow Singapore's Personal Data Protection Act (PDPA). If you're in a place with GDPR-style rights, such as
            the EU or the UK, you have those rights too, including to object to how we use your data and to complain to your
            local data protection authority.
          </p>
        </>
      ),
    },
    {
      id: 'security',
      title: 'Security',
      body: (
        <p>
          We store passwords only as hashes, keep machines isolated from each other, and rate-limit sign-ins. No system is
          perfectly secure. If you find a problem, please tell us at <Email />.
        </p>
      ),
    },
    {
      id: 'children',
      title: 'Children',
      body: <p>cmx is for adults. You must be 18 or older, or the age of majority where you live, to have an account.</p>,
    },
    {
      id: 'changes',
      title: 'Changes to this policy',
      body: (
        <p>
          We'll post any update here and change the date at the top. If a change matters to you, we'll email you before it
          takes effect.
        </p>
      ),
    },
    {
      id: 'contact',
      title: 'Contact',
      body: (
        <p>
          Questions about your data: <Email />. {COMPANY}, Singapore.
        </p>
      ),
    },
  ],
};

// ---------------------------------------------------------------------------------------------
// Acceptable Use Policy
// ---------------------------------------------------------------------------------------------

const AUP: Doc = {
  path: '/acceptable-use',
  nav: 'Acceptable use',
  name: 'Acceptable Use Policy',
  title: (
    <>
      Acceptable <em>Use</em>
    </>
  ),
  intro: (
    <>
      Your cmx machine is a real computer, and you're root on it. This policy lists the few things you can't do with it, so
      cmx stays fast, safe and available for everyone. It's part of our <L to="/terms">Terms of Service</L>.
    </>
  ),
  summary: [
    <>Build, run and experiment freely. That's what cmx is for.</>,
    <>Don't harm others: no spam, no attacks, no malware, no mining.</>,
    <>Zero tolerance for child sexual abuse material. We report it.</>,
    <>The rules cover what your AI agents do, too.</>,
    <>Breaking them can get your cloud machine suspended or your account closed.</>,
  ],
  sections: [
    {
      id: 'welcome',
      title: 'What’s welcome',
      body: (
        <ul>
          <li>Running AI coding agents, around the clock.</li>
          <li>Dev servers, builds and tests.</li>
          <li>Personal projects and experiments.</li>
          <li>Sharing preview links with the people you work with.</li>
          <li>Using the CPU, memory and disk your plan includes.</li>
        </ul>
      ),
    },
    {
      id: 'not-allowed',
      title: 'What’s not allowed',
      body: (
        <ul className="lg-rules">
          <li>
            <b>Cryptocurrency mining</b> of any kind.
          </li>
          <li>
            <b>Spam or bulk email.</b> Outgoing mail ports 25, 465 and 587 are blocked.
          </li>
          <li>
            <b>Attacking other systems:</b> scanning, probing, breaking in, or denial-of-service (DDoS) attacks.
          </li>
          <li>
            <b>Malware, phishing or illegal content:</b> hosting it or spreading it.
          </li>
          <li>
            <b>Child sexual abuse material (CSAM).</b> Zero tolerance. We close the account and report it to the
            authorities.
          </li>
          <li>
            <b>Infringing intellectual property:</b> other people's copyrights, trademarks or other rights.
          </li>
          <li>
            <b>Getting around limits:</b> circumventing your plan's limits or the isolation between machines.
          </li>
          <li>
            <b>Reselling</b> machines or access to them without our permission.
          </li>
          <li>
            <b>Public proxies or VPN exit nodes</b> for other people.
          </li>
          <li>
            <b>Excessive load</b> that harms other customers. We may throttle it.
          </li>
        </ul>
      ),
    },
    {
      id: 'agents',
      title: 'Your AI agents',
      body: (
        <p>
          These rules apply to everything that runs on your cloud machine, including agents acting for you. If an agent starts
          scanning networks or sending bulk email, that's a breach, just as if you'd done it yourself. Keep an eye on what
          your agents are allowed to do.
        </p>
      ),
    },
    {
      id: 'enforcement',
      title: 'What happens if the rules are broken',
      body: (
        <>
          <p>Depending on how serious it is, we may:</p>
          <ul>
            <li>send you a warning,</li>
            <li>throttle or suspend your cloud machine, or</li>
            <li>close your account.</li>
          </ul>
          <p>
            For serious abuse we may act straight away, without a warning, and without a refund. To investigate, we may
            look at your cloud machine, as described in <L to="/terms#content">Terms: Your content</L>. If you think we got it
            wrong, write to us at <Email />.
          </p>
        </>
      ),
    },
    {
      id: 'report',
      title: 'Reporting abuse',
      body: (
        <p>
          Seen a cmx machine misbehaving? Email <Email /> with what happened, when, and any addresses or links involved. We
          read every report.
        </p>
      ),
    },
  ],
};

const DOCS: Record<LegalDoc, Doc> = { terms: TERMS, privacy: PRIVACY, 'acceptable-use': AUP };
const ORDER: LegalDoc[] = ['terms', 'privacy', 'acceptable-use'];

/** Deep links (/terms#billing) land on their section once the page has rendered. */
function useHashScroll(doc: LegalDoc) {
  useEffect(() => {
    const id = location.hash.slice(1);
    if (!id) return;
    const t = requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
    return () => cancelAnimationFrame(t);
  }, [doc]);
}

export function LegalPage({ doc }: { doc: LegalDoc }) {
  const d = DOCS[doc];
  const others = ORDER.filter((k) => k !== doc).map((k) => DOCS[k]);
  useHashScroll(doc);

  return (
    <HxPage
      testid={`legal-${doc}`}
      top={
        <nav className="lg-nav" aria-label="Legal documents">
          {ORDER.map((k) => (
            <Link key={k} to={DOCS[k].path} className="lg-nav-link" aria-current={k === doc ? 'page' : undefined}>
              {DOCS[k].nav}
            </Link>
          ))}
        </nav>
      }
    >
      <article className="lg">
        <header className="lg-head">
          <span className="hx-kicker hx-rise" style={{ ['--i' as string]: 0 }}>
            Legal
          </span>
          <h1 className="hx-h1 hx-rise" style={{ ['--i' as string]: 1 }}>
            {d.title}
          </h1>
          <p className="lg-updated hx-rise" style={{ ['--i' as string]: 2 }}>
            Last updated {UPDATED}
          </p>
          <p className="lg-intro hx-rise" style={{ ['--i' as string]: 2 }}>
            {d.intro}
          </p>
        </header>

        <aside className="lg-short hx-rise" style={{ ['--i' as string]: 3 }} aria-label="In short">
          <h2 className="lg-short-title">In short</h2>
          <ul>
            {d.summary.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </aside>

        <nav className="lg-toc hx-rise" style={{ ['--i' as string]: 4 }} aria-label="Contents">
          <ol>
            {d.sections.map((s, i) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>
                  <span className="lg-num">{String(i + 1).padStart(2, '0')}</span>
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="lg-sections">
          {d.sections.map((s, i) => (
            <section key={s.id} id={s.id} className="lg-section" aria-labelledby={`${s.id}-h`}>
              <h2 id={`${s.id}-h`}>
                <a href={`#${s.id}`}>
                  <span className="lg-num">{String(i + 1).padStart(2, '0')}</span>
                  {s.title}
                </a>
              </h2>
              {s.body}
            </section>
          ))}
        </div>

        <footer className="lg-foot">
          <p>
            Questions? Write to <Email />.
          </p>
          <p className="lg-foot-links">
            Also read{' '}
            {others.map((o, i) => (
              <span key={o.path}>
                {i > 0 && ' and '}
                <Link to={o.path}>{o.name}</Link>
              </span>
            ))}
            .
          </p>
          <p className="lg-foot-co">
            © {new Date().getFullYear()} {COMPANY} · Singapore
          </p>
        </footer>
      </article>
    </HxPage>
  );
}
